/**
 *
 * OktaVerifyStepTwo Component
 *
 */

import React from "react";
import classnames from "classnames";
import {
  Button,
  TextField,
  stylize,
  Enums,
} from "@envestnet/envreact-component-library";
import withIntl from "utils/withIntl";
import messages from "./messages";
import styles, { Styles } from "./styles";
import { AppTypeEnum, StatusTypeEnum } from "./enums";

const { ButtonSize, ButtonStyle } = Enums;

interface InjectedProps extends Styles {
  /** Internal - injected from withIntl */
  formatMessage: FormatMessage;

  /** Internal - injected from WithStyles */
  innerRef?: React.RefObject<HTMLDivElement>;
}

interface DefaultProps {
  // Default props should always be required props, i.e. no ? indicating it's optional
  // The default values are specified below
  onCancel: Function;
  onBack: Function;
  onSubmitPassCode: Function;
  resetSubmissionStatus: Function;
  processingStatus: string;
}

export interface OktaVerifyStepTwoProps extends DefaultProps {
  /** CSS class name to be applied to top level component */
  className?: string;

  /** Inline style object to be applied to top level component */
  style?: React.CSSProperties;
}

const defaultProps: DefaultProps = {
  onCancel: () => {},
  onBack: () => {},
  onSubmitPassCode: () => {},
  resetSubmissionStatus: () => {},
  processingStatus: StatusTypeEnum.INITIAL,
};

type Props = InjectedProps & OktaVerifyStepTwoProps;

const OktaVerifyStepTwo = (props: Props) => {
  const {
    classes,
    style,
    className,
    formatMessage,
    innerRef,
    onCancel,
    onBack,
    onSubmitPassCode,
    processingStatus,
    resetSubmissionStatus,
  } = props;

  const [code, setCode] = React.useState("");
  const [passcodeError, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (processingStatus === StatusTypeEnum.ERROR) {
      setError(formatMessage(messages.invalidCodeErrorMessage));
    } else {
      setError(null);
    }
  }, [processingStatus]);

  React.useEffect(
    () => () => {
      resetSubmissionStatus();
    },
    []
  );

  return (
    <div
      ref={innerRef}
      className={classnames(className, classes.container)}
      style={style}
    >
      <div data-testid="okta-verify-step-two-code-input">
        <TextField
          label={formatMessage(messages.codeTextField)}
          text={code}
          textStyle={{fontFamily: "Red Hat Text"}}
          onChangeText={(text) => setCode(text)}
          errorMessage={passcodeError}
          className={classnames(
            classes.textfield,
            "header-font-family",
            "no-border-radius"
          )}
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Link}
          buttonSize={ButtonSize.Regular}
          className={classnames(classes.linkButton, "header-text-font-family")}
          name={formatMessage(messages.backButtonLabel)}
          onClick={() => {
            onBack();
          }}
          aria-label="challenge question submit button"
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Primary}
          buttonSize={ButtonSize.Regular}
          className={classnames(classes.verifyButton, "no-border-radius", "header-text-font-family")}
          name={formatMessage(messages.verifyButtonLabel)}
          onClick={() => {
            onSubmitPassCode({
              authenticatorType: AppTypeEnum.OKTA,
              passcode: code,
            });
          }}
          disabled={!code}
          loading={processingStatus === StatusTypeEnum.PROCESSING}
          aria-label="challenge question submit button"
        />
      </div>
      <div>
        <Button
          buttonStyle={ButtonStyle.Link}
          buttonSize={ButtonSize.Regular}
          className={classnames(classes.linkButton, "header-text-font-family")}
          name={formatMessage(messages.cancelButtonLabel)}
          onClick={() => {
            onCancel();
          }}
          aria-label="challenge question submit button"
        />
      </div>
    </div>
  );
};

OktaVerifyStepTwo.defaultProps = defaultProps;

export default withIntl(
  stylize<Props, DefaultProps>(styles)(OktaVerifyStepTwo)
);
